/**
 * Building transfer — pure import logic.
 *
 * Side-effect-free helpers for the import flow: manifest parsing and version
 * gating, ZIP-entry path confinement, summary write planning around write-once
 * rules, and config-reference collection for the preview. Kept pure so they
 * are unit-tested without Firebase.
 *
 * Adapted from mf-functions branch `feat/building-transfer`
 * (`functions/src/buildings/transfer/importLogic.ts`); the server-side ZIP
 * path validation is replaced by local-file parsing, and the version-exact
 * gate is relaxed per decision 2026-09-01: schema version blocks, app version
 * only warns.
 */
import {
    BUNDLE_PATHS,
    EXPORT_SCHEMA_VERSION,
    BuildingExportManifest,
    ExportedUsers,
    bundleStoragePrefixes,
} from './bundleFormat';

const isRecord = (value: unknown): value is Record<string, unknown> =>
    !!value && typeof value === 'object' && !Array.isArray(value);

/**
 * Parse and gate manifest.json. Throws (Swedish, user-facing) on structural
 * problems or an unsupported bundle layout version. An appVersion mismatch is
 * NOT checked here — it warns in the preview instead of blocking.
 */
export function parseManifest(text: string): BuildingExportManifest {
    let parsed: unknown;
    try {
        parsed = JSON.parse(text);
    } catch {
        throw new Error('Filens manifest kunde inte läsas som JSON');
    }
    if (!isRecord(parsed) || typeof parsed.exportSchemaVersion !== 'number') {
        throw new Error('Filen är inte en Molnfastigheter-byggnadsexport');
    }
    if (parsed.exportSchemaVersion !== EXPORT_SCHEMA_VERSION) {
        throw new Error(
            'Filen använder en okänd exportversion och kan inte importeras av denna version av systemet'
        );
    }
    if (typeof parsed.buildingKey !== 'string' || parsed.buildingKey.length === 0) {
        throw new Error('Filens manifest saknar byggnadsnyckel');
    }
    if (typeof parsed.buildingName !== 'string' || typeof parsed.appVersion !== 'string') {
        throw new Error('Filens manifest är ofullständigt');
    }
    return parsed as unknown as BuildingExportManifest;
}

/**
 * Map a bundle ZIP entry name to its destination Storage path, or null if the
 * entry is not a binary or is not confined to this building's prefixes
 * (`buildings/{key}/` and `buildingsSummary/{key}/` — cover photos live under
 * the latter). Binaries are stored under `binaries/<storagePath>`. The
 * destination MUST land under an allowed prefix and contain no `..` — this
 * rejects zip-slip / cross-building entry names in a crafted bundle that would
 * otherwise let an import write to arbitrary bucket objects.
 */
export function bundleEntryToStoragePath(entryName: string, buildingKey: string): string | null {
    if (!entryName.startsWith(BUNDLE_PATHS.binariesDir)) {
        return null;
    }
    const path = entryName.slice(BUNDLE_PATHS.binariesDir.length);
    if (path.length === 0 || path.includes('..')) {
        return null;
    }
    if (!bundleStoragePrefixes(buildingKey).some(prefix => path.startsWith(prefix))) {
        return null;
    }
    return path;
}

/**
 * Summary fields that are write-once in the database rules
 * (`.validate: !data.exists()`). When the target already has a value, the
 * import must not touch these paths — a write would be rejected.
 */
export const WRITE_ONCE_SUMMARY_FIELDS = ['createdBy', 'createdWhen'] as const;

/**
 * Plan the `buildingsSummary/{key}` write as per-field multi-path updates:
 * every bundled field is written, existing fields absent from the bundle are
 * removed, and write-once fields already present in the target are skipped
 * (kept as-is) so the rules don't reject the whole atomic update.
 */
export function buildSummaryUpdates(
    buildingKey: string,
    newSummary: unknown,
    existingSummary: unknown
): Record<string, unknown> {
    const updates: Record<string, unknown> = {};
    const next = isRecord(newSummary) ? newSummary : {};
    const existing = isRecord(existingSummary) ? existingSummary : {};
    const writeOnce = new Set<string>(WRITE_ONCE_SUMMARY_FIELDS);

    for (const [field, value] of Object.entries(next)) {
        if (writeOnce.has(field) && existing[field] !== undefined) {
            continue;
        }
        updates[`buildingsSummary/${buildingKey}/${field}`] = value;
    }
    for (const field of Object.keys(existing)) {
        if (field in next || writeOnce.has(field)) {
            continue;
        }
        updates[`buildingsSummary/${buildingKey}/${field}`] = null;
    }
    return updates;
}

/** Config catalog keys a bundled building references in the target instance. */
export interface ConfigReferences {
    /** `config/buildComponentTypes/{key}` via component `specificType.value`. */
    componentTypeKeys: string[];
    /** `config/materials/{key}` via component `material.value[]`. */
    materialKeys: string[];
    /** `buildingTags/{key}` via summary `groupTags/{key}`. */
    tagKeys: string[];
}

export function collectConfigReferences(building: unknown, summary: unknown): ConfigReferences {
    const componentTypeKeys = new Set<string>();
    const materialKeys = new Set<string>();
    const tagKeys = new Set<string>();

    const structure = isRecord(building) ? building.structure : undefined;
    const components = isRecord(structure) ? structure.components : undefined;
    if (isRecord(components)) {
        for (const component of Object.values(components)) {
            if (!isRecord(component)) continue;
            const specificType = component.specificType;
            if (isRecord(specificType) && typeof specificType.value === 'string' && specificType.value) {
                componentTypeKeys.add(specificType.value);
            }
            const material = component.material;
            if (isRecord(material) && Array.isArray(material.value)) {
                for (const key of material.value) {
                    if (typeof key === 'string' && key) materialKeys.add(key);
                }
            }
        }
    }

    const groupTags = isRecord(summary) ? summary.groupTags : undefined;
    if (isRecord(groupTags)) {
        for (const key of Object.keys(groupTags)) tagKeys.add(key);
    }

    return {
        componentTypeKeys: [...componentTypeKeys],
        materialKeys: [...materialKeys],
        tagKeys: [...tagKeys],
    };
}

/** Referenced config keys missing from the target instance, for the preview warning. */
export interface MissingConfigKeys {
    componentTypeKeys: string[];
    materialKeys: string[];
    tagKeys: string[];
}

/**
 * Drawing nodes (level, roof-plan and parking blueprints) in a bundled building
 * that lack a PDF `source`. Returned as paths relative to `buildings/{key}`.
 * The database rules require `source` on every drawing, so the import refuses
 * a bundle with any such drawing (plan D17) rather than failing the atomic
 * building write with a permission error.
 */
export function findDrawingsWithoutSource(building: unknown): string[] {
    const missing: string[] = [];
    const checkMap = (blueprints: unknown, basePath: string): void => {
        if (!isRecord(blueprints)) return;
        for (const [key, node] of Object.entries(blueprints)) {
            if (!isRecord(node)) continue;
            const source = node.source;
            const hasSource =
                isRecord(source) && typeof source.storagePath === 'string' && source.storagePath.length > 0;
            if (!hasSource) missing.push(`${basePath}/${key}`);
        }
    };
    if (!isRecord(building)) return missing;

    const structure = isRecord(building.structure) ? building.structure : undefined;
    const levels = structure && isRecord(structure.levels) ? structure.levels : undefined;
    if (levels) {
        for (const [levelKey, level] of Object.entries(levels)) {
            if (isRecord(level)) checkMap(level.blueprints, `structure/levels/${levelKey}/blueprints`);
        }
    }
    const roofPlan = structure && isRecord(structure.roofPlan) ? structure.roofPlan : undefined;
    if (roofPlan) checkMap(roofPlan.blueprints, 'structure/roofPlan/blueprints');

    const parking = isRecord(building.parking) ? building.parking : undefined;
    if (parking) {
        for (const [areaKey, area] of Object.entries(parking)) {
            if (isRecord(area)) checkMap(area.blueprints, `parking/${areaKey}/blueprints`);
        }
    }
    return missing;
}

export interface BuildingImportPreview {
    buildingKey: string;
    buildingName: string;
    counts: BuildingExportManifest['counts'];
    buildingExists: boolean;
    /** Non-null when the bundle was produced by a different web app version. */
    appVersionWarning: string | null;
    referencedUsers: Array<{ uid: string; name: string; existsInTarget: boolean }>;
    missingConfig: MissingConfigKeys;
    skippedStoragePaths: string[];
}

/**
 * Assemble the preview from the manifest, the exported users, and facts read
 * from the target environment.
 */
export function buildImportPreview(
    manifest: BuildingExportManifest,
    exportedUsers: ExportedUsers,
    targetAppVersion: string,
    buildingExists: boolean,
    existingTargetUids: ReadonlySet<string>,
    missingConfig: MissingConfigKeys
): BuildingImportPreview {
    return {
        buildingKey: manifest.buildingKey,
        buildingName: manifest.buildingName,
        counts: manifest.counts,
        buildingExists,
        appVersionWarning:
            manifest.appVersion === targetAppVersion
                ? null
                : `Filen exporterades från version ${manifest.appVersion}, denna instans kör ${targetAppVersion}. Kontrollera resultatet extra noga.`,
        referencedUsers: Object.entries(exportedUsers).map(([uid, user]) => ({
            uid,
            name: user.name,
            existsInTarget: existingTargetUids.has(uid),
        })),
        missingConfig,
        skippedStoragePaths: manifest.skippedStoragePaths ?? [],
    };
}
